import { APIClient } from "../../helpers/api_helper";
import { getLoggedinUser } from "../../helpers/api_helper";

const api = new APIClient();

const ASSIGNMENTS_ENDPOINT = "/api/v1/admin/rbac/assignments";

export interface AssignRolePayload {
  user_id: string;
  role_code: string;
  scope_type: string;
  scope_ids: string[];
  created_by: string;
}

export interface UpsertAssignmentInput {
  assignment_id?: string;
  role_code: string;
  scope_type?: string;
  scope_ids?: string[];
}

export interface AssignmentItem {
  _id?: string;
  userId: string;
  roleCode: string;
  scopeType: string;
  scopeIds: string[];
  createdAt?: string;
  createdBy?: string;
  isActive?: boolean;
  userName: string;
  userEmail: string;
  userCountryCode?: number;
  userPhoneNumber?: number;
}

export interface AssignmentsMeta {
  total: number;
  page: number;
  limit: number;
  pages: number;
}

export interface ListAssignmentsResponse {
  data: AssignmentItem[];
  meta: AssignmentsMeta;
}

export interface ListAssignmentsParams {
  userId?: string;
  roleCode?: string;
  scopeType?: string;
  page?: number;
  limit?: number;
  isActive?: boolean;
  search?: string;
}

/**
 * Assign a role to a user.
 */
export const assignRoleToUser = async (
  userId: string,
  roleCode: string,
  scopeType: string,
  scopeIds: string[]
): Promise<void> => {
  const currentUser = getLoggedinUser();
  const createdBy =
    currentUser?.id ||
    currentUser?._id ||
    currentUser?.user_id ||
    currentUser?.userId ||
    "";

  const payload: AssignRolePayload = {
    user_id: userId,
    role_code: roleCode,
    scope_type: scopeType,
    scope_ids: scopeIds,
    created_by: createdBy,
  };

  await api.create(ASSIGNMENTS_ENDPOINT, payload);
};

/**
 * Upsert multiple role assignments for a user (create/update/delete in one payload).
 * Body shape:
 * {
 *   user_id: string;
 *   created_by: string;
 *   assignments: [
 *     { assignment_id?: string; role_code: string; scope_type?: string; scope_ids?: string[] }
 *   ]
 * }
 */
export const upsertUserAssignments = async (
  userId: string,
  assignments: UpsertAssignmentInput[]
): Promise<void> => {
  const currentUser = getLoggedinUser();
  const createdBy =
    currentUser?.id ||
    currentUser?._id ||
    (currentUser as any)?.user_id ||
    (currentUser as any)?.userId ||
    "";

  const body = {
    user_id: userId,
    created_by: createdBy,
    assignments: assignments.map((a) => ({
      assignment_id: a.assignment_id,
      role_code: a.role_code,
      scope_type: a.scope_type ?? "",
      scope_ids: a.scope_ids ?? [],
    })),
  };

  await api.create(ASSIGNMENTS_ENDPOINT, body);
};

/**
 * List role assignments (used for Access Management Users listing).
 * Supports filters: userId, roleCode, scopeType, and pagination: page, limit.
 */
export const listAssignments = async (
  params: ListAssignmentsParams
): Promise<ListAssignmentsResponse> => {
  const {
    userId,
    roleCode,
    scopeType,
    page = 1,
    limit = 20,
    isActive,
    search,
  } = params;

  const queryParams: Record<string, string | number> = { page, limit };

  if (userId && userId.trim()) {
    queryParams.userId = userId.trim();
  }
  if (roleCode && roleCode.trim()) {
    queryParams.roleCode = roleCode.trim();
  }
  if (scopeType && scopeType.trim()) {
    queryParams.scopeType = scopeType.trim();
  }
  if (typeof isActive === "boolean") {
    queryParams.isActive = isActive ? "true" : "false";
  }
  if (search && search.trim()) {
    queryParams.search = search.trim();
  }

  const queryString =
    "?" +
    Object.entries(queryParams)
      .map(
        ([key, value]) =>
          `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`
      )
      .join("&");

  const response = await api.get(`${ASSIGNMENTS_ENDPOINT}${queryString}`, undefined);

  if (response && typeof response === "object" && "data" in response && "meta" in response) {
    const data = response as ListAssignmentsResponse;
    return {
      data: Array.isArray(data.data) ? data.data : [],
      meta: data.meta,
    };
  }

  // Fallback if API shape is unexpected
  if (Array.isArray(response)) {
    const items = response as AssignmentItem[];
    return {
      data: items,
      meta: {
        total: items.length,
        page: 1,
        limit: items.length,
        pages: 1,
      },
    };
  }

  return {
    data: [],
    meta: {
      total: 0,
      page: 1,
      limit,
      pages: 0,
    },
  };
};

/**
 * Update user active status.
 * Endpoint: /api/v1/admin/auth/users/{user_id}/statu
 */
export const updateUserStatus = async (
  userId: string,
  isActive: boolean
): Promise<void> => {
  const currentUser = getLoggedinUser();
  const updatedBy =
    currentUser?.id ||
    currentUser?._id ||
    (currentUser as any)?.user_id ||
    (currentUser as any)?.userId ||
    "";

  await api.put(`/api/v1/admin/auth/users/${userId}/status`, {
    isActive,
    updated_by: updatedBy,
  });
};

